import { Response } from './index';
import { mgmtAxios, MgmtEnvelope, getMgmtMemberToken } from './mgmt';
// import { AxiosError } from 'axios';

// PG-178 券詳情改讀 Neil 的已領券(coupons/claimed),不再打舊版
interface MgmtClaimed {
  id: number;
  title: string | null;
  image_url: string | null;
  use_type: number;
  use_type_label: string | null;
  promotion_code: string | null;
  pin_code?: string | null;
  redeem_url: string | null;
  banner_url?: string | null;
  use_end_at: string | null;
  claimed_at: string;
}
const fetchClaimed = async (): Promise<MgmtClaimed[]> => {
  const token = getMgmtMemberToken();
  const { data } = await mgmtAxios.request<MgmtEnvelope<MgmtClaimed[]>>({
    url: '/api/member/coupons/claimed',
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  return data.details || [];
};

// PG-179 顧客歸還據點:新後端服務點 → n18 既有 ServicePointsData 形狀
interface MgmtServicePoint {
  id: number;
  channel_id: number | null;
  chs_short_code: string | null;
  channel_name: string | null;
  store_id: string;
  name: string;
  address: string;
  longitude: number;
  latitude: number;
  service_time: string | null;
  return_type: 'box' | 'staff';
  discount_type: string | null;
  city: string | null;
  logo_pin_url: string | null;
  // {"0": "AB", ..., "6": null}:每週各天的營業時段代碼,舊站是七個 week_Nxxx_ranges 欄位
  week_ranges?: Record<string, string | null> | null;
  visible?: boolean | number | null;
}
const weekRange = (s: MgmtServicePoint, day: string) => s.week_ranges?.[day] ?? undefined;
const mapMgmtServicePoint = (s: MgmtServicePoint) => ({
  id: s.id,
  latitude: Number(s.latitude),
  longitude: Number(s.longitude),
  name: s.name,
  address: s.address,
  service_time: s.service_time ?? '',
  channel_name: s.channel_name ?? '',
  enable: 1,
  store_id: s.store_id,
  chs_short_code: s.chs_short_code ?? '',
  return_type: s.return_type,
  discount_type: s.discount_type ?? '',
  distance: 0,
  visible: s.visible === undefined || s.visible === null ? 1 : Number(s.visible),
  chs_logo_pin_url: s.logo_pin_url,
  week_0sun_ranges: weekRange(s, '0'),
  week_1mon_ranges: weekRange(s, '1'),
  week_2tue_ranges: weekRange(s, '2'),
  week_3wed_ranges: weekRange(s, '3'),
  week_4thu_ranges: weekRange(s, '4'),
  week_5fri_ranges: weekRange(s, '5'),
  week_6sat_ranges: weekRange(s, '6'),
});
const fetchMgmtServicePoints = async (params: Record<string, unknown>) => {
  const { data } = await mgmtAxios.request<MgmtEnvelope<MgmtServicePoint[]>>({
    url: '/api/public/return/service-points',
    method: 'GET',
    params,
  });
  return (data.details || []).map(mapMgmtServicePoint);
};

export interface CouponsData {
  id: number;
  title: string;
  description: string;
  start_at: string;
  expired_at: string;
}

export interface CouponDetailData {
    id: number;
    title: string;
    description: string;
    specification: string;
    start_at: string;
    expired_at: string;
    created_at: string;
    picture_url: string;
    promotion_code: string;
    promotion_url: string;
    use_type: number;
    pin_code: string;
    banner_url: string;
}
/**
 * 取得(線上)電商優惠券(Coupons)!!
 */
export const getCoupons = async (
  _token: string,
  _brandID: number
): Promise<Response<CouponsData[]>> => {
    // PG-178 改讀新後端已領券
    const rows = await fetchClaimed();
    return {
      data: rows.map((c) => ({
        id: c.id,
        title: c.title ?? '',
        description: c.use_type_label ?? '',
        start_at: '',
        expired_at: c.use_end_at ?? '',
      })),
    };
};

export const getCouponDetail = async (
  _token: string,
  _userRentLogId: number
): Promise<Response<any>> => {
    // PG-178 改讀新後端已領券
    const rows = await fetchClaimed();
    const list: CouponDetailData[] = rows.map((c) => ({
      id: c.id,
      title: c.title ?? '',
      description: c.use_type_label ?? '',
      specification: '',
      start_at: '',
      expired_at: c.use_end_at ?? '',
      created_at: c.claimed_at ?? '',
      picture_url: c.image_url ?? '',
      promotion_code: c.promotion_code ?? '',
      promotion_url: c.redeem_url ?? '',
      use_type: c.use_type,
      pin_code: c.pin_code ?? '',
      banner_url: c.banner_url ?? '',
    }));
    return { data: { data: list } };
};

export interface ServicePointsData {
  chs_logo_pin_url?: any;
  id: number;
  latitude: number;
  longitude: number;
  name: string;
  address: string;
  service_time: string;
  channel_name: string;
  enable: number;
  store_id: string;
  chs_short_code: string; 
  // markerType: 'box' | 'staff';
  return_type: 'box' | 'staff';
  discount_type: string; 
  distance: number; 
  visible: number | boolean; 
  week_0sun_ranges?: any;
  week_1mon_ranges?: any;
  week_2tue_ranges?: any;
  week_3wed_ranges?: any;
  week_4thu_ranges?: any;
  week_5fri_ranges?: any;
  week_6sat_ranges?:any;
}
/**
 * 取得服務據點資訊(Info + GPS)!!
 */
// 公開 API:不需登入。後端已移除 auth:members,且 Controller/Service 皆未讀取 user 身份
export const listServicePoints = async (
  brandID: number,
  city?: string,
  returnType?: string,
  shop?: string,
  openTime?: string[],
  discountType?: string,
  latitude?: number,
  longitude?: number,
): Promise<Response<ServicePointsData[]>> => {
    // PG-179 改打新後端
    // 通路篩選送的是新後端 channel id(listChannels 的 value);'0' = 全部,不送
    const data = await fetchMgmtServicePoints({
      city,
      return_type: returnType,
      channel_id: shop && shop !== '0' ? shop : undefined,
    });
    return { data };
};

// 為了加速地圖讀取效率，簡化一版限制抓取離ＧＰＳ最近距離４００筆的ＡＰＩ，如果沒有抓到ＧＰＳ位置，則抓取全部資料
export interface RedislistServicePointsData {
  chs_logo_pin_url?: any;
  id: number;
  latitude: number;
  longitude: number;
  name: string;
  address: string;
  service_time: string;
  channel_name: string;
  enable: number;
  store_id: string; // 21.12.23 SCH
  chs_short_code: string; // 21.12.23 SCH
  // markerType: 'box' | 'staff';
  return_type: 'box' | 'staff'; // 22.05.17 SCH
  discount_type: string; // 22.05.16 SCH
  distance: number; // 22.05.19 SCH
  visible: number | boolean; // 22.05.21 SCH ●●●
}
/**
 * 取得服務據點資訊(Info + GPS)!!
 */
export const RedislistServicePoints = async (
  token: string,
  brandID: number,
  userID: number,
  latitude?: number, // 20230112 Wilson add
  longitude?: number, // 20230112 Wilson add
): Promise<Response<FamiServicePointsData[]>> => {
  try {
    // PG-179 改打新後端
    const data = await fetchMgmtServicePoints({});
    return { data: data as unknown as FamiServicePointsData[] };
  } catch (error: any) {
    console.log(error);
    return { error, data: undefined };
  }
};

// 20230313 wilson add end

// 20230112 wilson add start
// 因應全家通路做一個獨立API
export interface FamiServicePointsData {
  id: number;
  latitude: number;
  longitude: number;
  name: string;
  address: string;
  service_time: string;
  channel_name: string;
  enable: number;
  store_id: string; // 21.12.23 SCH
  chs_short_code: string; // 21.12.23 SCH
  // markerType: 'box' | 'staff';
  return_type: 'box' | 'staff'; // 22.05.17 SCH
  discount_type: string; // 22.05.16 SCH
  distance: number; // 22.05.19 SCH
  visible: number | boolean; // 22.05.21 SCH ●●●
  takeCount?: number,
  chs_logo_pin_url?: any;
}
/**
 * 取得服務據點資訊(Info + GPS)!!
 */
export const familistServicePoints = async (
  token: string,
  brandID: number,
  userID: number,
  shop?: string,
  latitude?: number, 
  longitude?: number,
): Promise<Response<FamiServicePointsData[]>> => {
    // PG-179 改打新後端(全家=fami 通路的服務點)
    const data = await fetchMgmtServicePoints({ return_type: 'staff' });
    return { data: data as unknown as FamiServicePointsData[] };
};


export interface ChannelsData {
  lavel: string;
  label: string;
  value: string;
}
/**
 * 取得通路清單(lavel,value)!!
 */
// 公開 API:不需登入
export const listChannels = async (
    brandID: number,
): Promise<Response<ChannelsData[]>> => {
    // PG-179 改打新後端
    const { data } = await mgmtAxios.request<MgmtEnvelope<{ id: number; short_code: string; name: string; alias: string }[]>>({
      url: '/api/public/return/channels',
      method: 'GET',
    });
    // Select 元件讀的是 label;value 要是 channel id,服務點端點的 channel_id 只吃整數
    const list = (data.details || []).map((c) => ({
      lavel: c.alias || c.name,
      label: c.alias || c.name,
      value: String(c.id),
    }));
    return { data: list };
};

// 22.05.16 SCH, Add
export interface VoucherInfoData {
  picture_url: string;
  description: string;
  title:string;
}
/**
 * 取得通路兌換券的(picture_url,description)!!
 */
export const getVoucherInfo = async (
    _token: string,
    _brandID: number,
    id: string,
): Promise<Response<VoucherInfoData>> => {
    // PG-178 據點專屬折扣預覽:改讀新後端可領券,依 id 找,找不到回空
    const token = getMgmtMemberToken();
    try {
      const { data } = await mgmtAxios.request<MgmtEnvelope<{ id: number; title: string; image_url: string | null; campaign_name: string | null }[]>>({
        url: '/api/member/coupons/available',
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const c = (data.details || []).find((r) => String(r.id) === String(id));
      return {
        data: {
          picture_url: c?.image_url ?? '',
          description: c?.campaign_name ?? '',
          title: c?.title ?? '',
        },
      };
    } catch {
      return { data: { picture_url: '', description: '', title: '' } };
    }
};

// 22.05.27 SCH, Add
export interface VoucherData {
  redeem_code: string;
  pin_code: string;
  use_type: number;
  start_at: string;
  start_use_at: string;
  expired_at: string;
  expired_use_at: string;
  title: string;
  description: string;
  specification: string;
  picture_url: string; // 22.05.30 SCH
  redeem_url: string; // 22.05.30 SCH
  chs_short_code: string; // 22.05.31 SCH
  used: number;
}
/**
 * 取得通路兌換券的(title,description)!!
 */
export const getVoucher = async (
  _token: string,
  _brandID: number,
  id: string,
): Promise<Response<VoucherData>> => {
    // PG-178 改讀新後端已領券,依 id 找出該張
    const rows = await fetchClaimed();
    const c = rows.find((r) => String(r.id) === String(id));
    return {
      data: {
        redeem_code: c?.promotion_code ?? '',
        pin_code: '',
        use_type: c?.use_type ?? 0,
        start_at: '',
        start_use_at: '',
        expired_at: c?.use_end_at ?? '',
        expired_use_at: c?.use_end_at ?? '',
        title: c?.title ?? '',
        description: c?.use_type_label ?? '',
        specification: '',
        picture_url: c?.image_url ?? '',
        redeem_url: c?.redeem_url ?? '',
        chs_short_code: '',
        used: 0,
      },
    };
};

